"use client"

import * as React from "react"
import { OpenAppTab, InstalledApp, App } from "@/types/workspace"
import { NotificationItem } from "@/types/notification"

export function getAppSlug(app?: { slug?: string | null; id?: string } | null): string {
  if (!app) return ""
  if (app.slug && app.slug.trim()) {
    const s = app.slug.trim().toLowerCase()
    return s.startsWith("bold-") && s !== "bold-system" ? s.replace(/^bold-/, "") : s
  }
  if (app.id) {
    const id = app.id.toLowerCase()
    if (id.startsWith("bold-") && id !== "bold-system") {
      return id.replace(/^bold-/, "")
    }
    return id
  }
  return ""
}

export function isAppMatch(
  ia: { app: { id?: string; slug?: string; name?: string } },
  term: string
): boolean {
  if (!term || !ia?.app) return false
  const t = term.toLowerCase().trim()
  const slug = (ia.app.slug || "").toLowerCase().trim()
  const id = (ia.app.id || "").toLowerCase().trim()
  const name = (ia.app.name || "").toLowerCase().trim()

  if (slug === t || id === t) return true
  if (slug.replace(/^bold-/, "") === t || id.replace(/^bold-/, "") === t) return true
  if (slug === `bold-${t}` || id === `bold-${t}`) return true
  if (name.replace(/\s+/g, "-") === t || name.replace(/\s+/g, "") === t) return true
  return false
}

export function resolveIframeUrl(baseUrl: string, subpath?: string) {
  if (!subpath || subpath === "blank" || subpath === "/blank") return baseUrl
  const cleanSubpath = subpath.startsWith("/") ? subpath : "/" + subpath
  try {
    const baseObj = new URL(baseUrl)
    const basePath = baseObj.pathname.replace(/\/$/, "")
    if (basePath && cleanSubpath.startsWith(basePath)) {
      return `${baseObj.origin}${cleanSubpath}`
    }
    if (cleanSubpath.startsWith(baseObj.pathname) || baseObj.pathname === "/") {
      return `${baseObj.origin}${cleanSubpath}`
    }
    return `${baseUrl.replace(/\/$/, '')}${cleanSubpath}`
  } catch (e) {
    return `${baseUrl.replace(/\/$/, '')}${cleanSubpath}`
  }
}

export function useAppRouterSync({
  activeAppId,
  setActiveAppId,
  splitActiveAppId,
  setSplitActiveAppId,
  focusedPane,
  openApps,
  setOpenApps,
  installedApps,
  setWidgetsConfig,
  setNotifications,
  playNotificationSound,
  isMobile,
  setSettingsTab,
  currentWorkspace,
  currentAccount,
  iframeSubpaths: externalIframeSubpaths
}: {
  activeAppId: string | null
  setActiveAppId: React.Dispatch<React.SetStateAction<string | null>>
  splitActiveAppId: string | null
  setSplitActiveAppId: React.Dispatch<React.SetStateAction<string | null>>
  focusedPane: "left" | "right"
  openApps: OpenAppTab[]
  setOpenApps: React.Dispatch<React.SetStateAction<OpenAppTab[]>>
  installedApps: InstalledApp[]
  setWidgetsConfig: React.Dispatch<React.SetStateAction<any[]>>
  setNotifications: React.Dispatch<React.SetStateAction<NotificationItem[]>>
  playNotificationSound: () => void
  isMobile: boolean
  setSettingsTab: (tab: any) => void
  currentWorkspace?: any
  currentAccount?: any
  iframeSubpaths?: any
}) {
  const internalIframeSubpaths = React.useRef<Record<string, string>>({})
  const iframeSubpaths = externalIframeSubpaths || internalIframeSubpaths

  // Hydrate iframeSubpaths from openApps or localStorage on mount/workspace change
  React.useEffect(() => {
    if (typeof window === "undefined") return
    if (currentWorkspace?.id) {
      try {
        const saved = localStorage.getItem(`bold_app_subpaths_${currentWorkspace.id}`)
        if (saved) {
          const parsed = JSON.parse(saved)
          if (parsed && typeof parsed === "object") {
            Object.assign(iframeSubpaths.current, parsed)
          }
        }
      } catch (e) {}
    }
    openApps.forEach((tab) => {
      if (tab.subpath) {
        iframeSubpaths.current[tab.instanceId] = tab.subpath
      }
    })
  }, [currentWorkspace?.id, openApps])

  const updateAppSubpath = React.useCallback(
    (instanceId: string, subpath: string) => {
      let cleanSub = subpath.trim()
      if (cleanSub.includes("://") || cleanSub.startsWith("//")) {
        try {
          const u = new URL(cleanSub, window.location.origin)
          cleanSub = u.pathname + u.search + u.hash
        } catch (e) {}
      }

      const targetTab = openApps.find((t) => t.instanceId === instanceId) || openApps.find((t) => t.instanceId === activeAppId)
      if (targetTab) {
        const baseApp = installedApps.find((ia) => isAppMatch(ia, targetTab.app.slug || targetTab.app.id))?.app
        const baseIframeUrl = targetTab.baseIframeUrl || baseApp?.iframeUrl || targetTab.app.iframeUrl
        try {
          if (baseIframeUrl) {
            const origUrl = new URL(baseIframeUrl)
            const basePath = origUrl.pathname.replace(/\/$/, "")
            if (basePath && cleanSub.startsWith(basePath)) {
              cleanSub = cleanSub.slice(basePath.length)
            }
          }
        } catch (e) {}
      }

      if (!cleanSub.startsWith("/")) cleanSub = "/" + cleanSub
      if (cleanSub === "/" || cleanSub === "/blank") cleanSub = ""

      const effectiveInstanceId = targetTab ? targetTab.instanceId : instanceId
      iframeSubpaths.current[effectiveInstanceId] = cleanSub

      if (targetTab) {
        setOpenApps((prev) =>
          prev.map((t) =>
            t.instanceId === effectiveInstanceId
              ? {
                  ...t,
                  subpath: cleanSub
                }
              : t
          )
        )

        if (typeof window !== "undefined" && currentWorkspace?.id) {
          try {
            localStorage.setItem(`bold_subpath_${currentWorkspace.id}_${targetTab.app.id}`, cleanSub)
            const appSlug = getAppSlug(targetTab.app)
            if (appSlug) {
              localStorage.setItem(`bold_subpath_${currentWorkspace.id}_${appSlug}`, cleanSub)
            }
            localStorage.setItem(`bold_app_subpaths_${currentWorkspace.id}`, JSON.stringify(iframeSubpaths.current))
          } catch (e) {}
        }

        if (typeof window !== "undefined" && currentAccount?.slug && currentWorkspace?.slug) {
          const isTargetActive = effectiveInstanceId === activeAppId || (!activeAppId && openApps.length === 1)
          if (isTargetActive) {
            const accSlug = currentAccount.slug
            const wsSlug = currentWorkspace.slug
            const appSlug = getAppSlug(targetTab.app)
            const trimmed = cleanSub.startsWith("/") ? cleanSub.slice(1) : cleanSub
            const fullPath = trimmed ? `/${accSlug}/${wsSlug}/${appSlug}/${trimmed}` : `/${accSlug}/${wsSlug}/${appSlug}`
            if (window.location.pathname + window.location.search + window.location.hash !== fullPath) {
              window.history.replaceState({ path: fullPath }, "", fullPath)
            }
          }
        }
      }
    },
    [openApps, installedApps, currentWorkspace, currentAccount, activeAppId, setOpenApps]
  )

  const handleOpenApp = React.useCallback((app: App, subpath?: string) => {
    const installed = installedApps.find((ia) => isAppMatch(ia, app.slug || app.id))
    if (installed && installed.status === "inactive") {
      return
    }

    const safeSlug = getAppSlug(app)
    const appWithSlug = { ...app, slug: safeSlug }

    let cleanSubpath = ""
    if (subpath && subpath !== "blank" && subpath !== "/blank") {
      cleanSubpath = subpath.startsWith("/") ? subpath : "/" + subpath
    }

    const existing = openApps.find(
      (t) => t.app.id === appWithSlug.id || isAppMatch({ app: t.app }, appWithSlug.slug)
    )

    // If subpath wasn't passed, check existing tab subpath, in-memory ref, or localStorage
    if (!cleanSubpath) {
      if (existing?.subpath) {
        cleanSubpath = existing.subpath
      } else if (existing && iframeSubpaths.current[existing.instanceId]) {
        cleanSubpath = iframeSubpaths.current[existing.instanceId]
      } else if (typeof window !== "undefined" && currentWorkspace?.id) {
        cleanSubpath =
          localStorage.getItem(`bold_subpath_${currentWorkspace.id}_${safeSlug}`) ||
          localStorage.getItem(`bold_subpath_${currentWorkspace.id}_${appWithSlug.id}`) ||
          ""
      }
    }

    const baseAppUrl = installed?.app?.iframeUrl || app.iframeUrl
    const finalIframeUrl = cleanSubpath ? resolveIframeUrl(baseAppUrl, cleanSubpath) : baseAppUrl

    if (existing) {
      if (cleanSubpath) {
        iframeSubpaths.current[existing.instanceId] = cleanSubpath
        if (typeof window !== "undefined" && currentWorkspace?.id) {
          try {
            localStorage.setItem(`bold_subpath_${currentWorkspace.id}_${appWithSlug.id}`, cleanSubpath)
            localStorage.setItem(`bold_subpath_${currentWorkspace.id}_${safeSlug}`, cleanSubpath)
            localStorage.setItem(`bold_app_subpaths_${currentWorkspace.id}`, JSON.stringify(iframeSubpaths.current))
          } catch (e) {}
        }
      }

      setOpenApps((prev) =>
        prev.map((t) =>
          t.instanceId === existing.instanceId
            ? {
                ...t,
                baseIframeUrl: baseAppUrl,
                subpath: cleanSubpath,
                app: { ...t.app, iframeUrl: finalIframeUrl }
              }
            : t
        )
      )

      try {
        const iframe = document.querySelector(`iframe[data-instance-id="${existing.instanceId}"]`) as HTMLIFrameElement
        if (iframe) {
          if (cleanSubpath && iframe.contentWindow) {
            iframe.contentWindow.postMessage({ type: "BOLD_NAVIGATE", payload: { path: cleanSubpath, url: finalIframeUrl }, path: cleanSubpath }, "*")
          }
          if (finalIframeUrl && iframe.src !== finalIframeUrl) {
            iframe.src = finalIframeUrl
          }
        }
      } catch (e) {}

      if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId(existing.instanceId)
        } else {
          setSplitActiveAppId(existing.instanceId)
        }
      } else {
        setActiveAppId(existing.instanceId)
      }

      // Sync URL immediately
      if (typeof window !== "undefined" && currentAccount?.slug && currentWorkspace?.slug) {
        const accSlug = currentAccount.slug
        const wsSlug = currentWorkspace.slug
        const trimmed = cleanSubpath.startsWith("/") ? cleanSubpath.slice(1) : cleanSubpath
        const fullPath = trimmed ? `/${accSlug}/${wsSlug}/${safeSlug}/${trimmed}` : `/${accSlug}/${wsSlug}/${safeSlug}`
        if (window.location.pathname + window.location.search + window.location.hash !== fullPath) {
          window.history.replaceState({ path: fullPath }, "", fullPath)
        }
      }
      return
    }

    const instanceId = Math.random().toString(36).substring(2, 9)
    if (cleanSubpath) {
      iframeSubpaths.current[instanceId] = cleanSubpath
      if (typeof window !== "undefined" && currentWorkspace?.id) {
        try {
          localStorage.setItem(`bold_subpath_${currentWorkspace.id}_${appWithSlug.id}`, cleanSubpath)
          localStorage.setItem(`bold_subpath_${currentWorkspace.id}_${safeSlug}`, cleanSubpath)
          localStorage.setItem(`bold_app_subpaths_${currentWorkspace.id}`, JSON.stringify(iframeSubpaths.current))
        } catch (e) {}
      }
    }

    const newTab: OpenAppTab = {
      instanceId,
      subpath: cleanSubpath,
      baseIframeUrl: baseAppUrl,
      app: {
        ...appWithSlug,
        iframeUrl: finalIframeUrl
      },
      pinned: false,
      groupId: null
    }

    setOpenApps((prev) => [...prev, newTab])

    if (splitActiveAppId !== null) {
      if (focusedPane === "left") {
        setActiveAppId(instanceId)
      } else {
        setSplitActiveAppId(instanceId)
      }
    } else {
      setActiveAppId(instanceId)
    }

    // Sync URL immediately
    if (typeof window !== "undefined" && currentAccount?.slug && currentWorkspace?.slug) {
      const accSlug = currentAccount.slug
      const wsSlug = currentWorkspace.slug
      const trimmed = cleanSubpath.startsWith("/") ? cleanSubpath.slice(1) : cleanSubpath
      const fullPath = trimmed ? `/${accSlug}/${wsSlug}/${safeSlug}/${trimmed}` : `/${accSlug}/${wsSlug}/${safeSlug}`
      if (window.location.pathname + window.location.search + window.location.hash !== fullPath) {
        window.history.replaceState({ path: fullPath }, "", fullPath)
      }
    }
  }, [openApps, splitActiveAppId, focusedPane, setOpenApps, setActiveAppId, setSplitActiveAppId, resolveIframeUrl, installedApps, currentWorkspace, currentAccount])

  const handleOpenWidgetAsApp = React.useCallback((widgetId: string, name: string, icon: string) => {
    const existing = openApps.find(t => t.app.id === widgetId)
    if (existing) {
      setActiveAppId(existing.instanceId)
      return
    }
    const dummyApp: App = {
      id: widgetId,
      name: name,
      slug: widgetId,
      description: `Widget ${name} do Sistema`,
      iconUrl: `emoji:${icon}`,
      iframeUrl: ""
    }
    const instanceId = Math.random().toString(36).substring(2, 9)
    const newTab: OpenAppTab = {
      instanceId,
      app: dummyApp,
      pinned: false,
      groupId: null
    }
    setOpenApps((prev) => [...prev, newTab])
    setActiveAppId(instanceId)
    setSplitActiveAppId(null)
  }, [openApps, setActiveAppId, setOpenApps, setSplitActiveAppId])

  const handleCloseApp = React.useCallback((instanceId: string) => {
    if (iframeSubpaths.current) {
      delete iframeSubpaths.current[instanceId]
    }

    const nextApps = openApps.filter((a) => a.instanceId !== instanceId)
    setOpenApps(nextApps)

    if (activeAppId === instanceId) {
      if (nextApps.length > 0) {
        setActiveAppId(nextApps[nextApps.length - 1].instanceId)
      } else {
        setActiveAppId(null)
      }
    }

    if (splitActiveAppId === instanceId) {
      setSplitActiveAppId(null)
    }
  }, [openApps, activeAppId, splitActiveAppId, setActiveAppId, setSplitActiveAppId, setOpenApps])

  const handleCloseAllApps = React.useCallback(() => {
    if (iframeSubpaths.current) {
      iframeSubpaths.current = {}
    }
    setOpenApps([])
    setActiveAppId(null)
    setSplitActiveAppId(null)
  }, [setActiveAppId, setOpenApps, setSplitActiveAppId])

  const handleOpenSettingsTab = React.useCallback(() => {
    const existingTab = openApps.find((t) => t.app.id === "settings")
    setSettingsTab(isMobile ? "menu" : "company")
    if (existingTab) {
      if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId(existingTab.instanceId)
        } else {
          setSplitActiveAppId(existingTab.instanceId)
        }
      } else {
        setActiveAppId(existingTab.instanceId)
      }
    } else {
      const settingsApp: App = {
        id: "settings",
        name: "Ajustes do Sistema",
        slug: "settings",
        description: "Configurações globais do sistema",
        iconUrl: "",
        iframeUrl: ""
      }
      const newTab: OpenAppTab = {
        instanceId: "settings",
        app: settingsApp,
        pinned: false,
        groupId: null
      }
      setOpenApps((prev) => [...prev, newTab])
      if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId("settings")
        } else {
          setSplitActiveAppId("settings")
        }
      } else {
        setActiveAppId("settings")
      }
    }
  }, [openApps, splitActiveAppId, focusedPane, isMobile, setSettingsTab, setActiveAppId, setSplitActiveAppId, setOpenApps])

  const handleOpenProfileTab = React.useCallback(() => {
    const existingTab = openApps.find((t) => t.app.id === "profile")
    if (existingTab) {
      if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId(existingTab.instanceId)
        } else {
          setSplitActiveAppId(existingTab.instanceId)
        }
      } else {
        setActiveAppId(existingTab.instanceId)
      }
    } else {
      const profileApp: App = {
        id: "profile",
        name: "Meu Perfil",
        slug: "profile",
        description: "Gerenciamento de perfil e credenciais",
        iconUrl: "emoji:👤",
        iframeUrl: ""
      }
      const newTab: OpenAppTab = {
        instanceId: "profile",
        app: profileApp,
        pinned: false,
        groupId: null
      }
      setOpenApps((prev) => [...prev, newTab])
      if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId("profile")
        } else {
          setSplitActiveAppId("profile")
        }
      } else {
        setActiveAppId("profile")
      }
    }
  }, [openApps, splitActiveAppId, focusedPane, setActiveAppId, setSplitActiveAppId, setOpenApps])

  const handleOpenMarketplaceTab = React.useCallback(() => {
    const existingTab = openApps.find((t) => t.app.id === "marketplace")
    if (existingTab) {
      if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId(existingTab.instanceId)
        } else {
          setSplitActiveAppId(existingTab.instanceId)
        }
      } else {
        setActiveAppId(existingTab.instanceId)
      }
    } else {
      const marketplaceApp: App = {
        id: "marketplace",
        name: "Loja de Aplicativos",
        slug: "marketplace",
        description: "Catálogo de aplicativos e extensões da plataforma Bold",
        iconUrl: "emoji:🛒",
        iframeUrl: ""
      }
      const newTab: OpenAppTab = {
        instanceId: "marketplace",
        app: marketplaceApp,
        pinned: false,
        groupId: null
      }
      setOpenApps((prev) => [...prev, newTab])
      if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId("marketplace")
        } else {
          setSplitActiveAppId("marketplace")
        }
      } else {
        setActiveAppId("marketplace")
      }
    }
  }, [openApps, splitActiveAppId, focusedPane, setActiveAppId, setSplitActiveAppId, setOpenApps])

  return {
    iframeSubpaths,
    updateAppSubpath,
    resolveIframeUrl,
    handleOpenSettingsTab,
    handleOpenProfileTab,
    handleOpenMarketplaceTab,
    handleOpenApp,
    handleOpenWidgetAsApp,
    handleCloseApp,
    handleCloseAllApps
  }
}
